import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Trash2, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { ChampionshipDetail, ChampionshipTeam, ChampionshipTeamDetail } from "@/types";
import { MATCH_STATUS_LABEL, apiError, championshipTone, formatDateTime, matchLabel, personName, teamLabel } from "@/lib/championships";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { PlayerPicker, type PlayerOption, type PlayerRef } from "@/components/championships/PlayerPicker";

function toPlayerOption(p: ChampionshipTeam["jugador1"]): PlayerOption {
  return { id: p.id, nombre: p.nombre, apellidos: p.apellidos, clubId: p.clubId, club: { nombre: "Jugador actual" } };
}

// Ficha de una pareja inscrita: aquí se edita (nombre y jugadores) o se retira del campeonato, y se
// ven sus partidos. Pinchar una pareja en el listado del campeonato lleva aquí.
export function ChampionshipTeamPage() {
  const params = useParams();
  const campeonatoId = Number(params.id);
  const equipoId = Number(params.equipoId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: campeonato } = useQuery({
    queryKey: ["championship", campeonatoId, "detail"],
    queryFn: async () => (await api.get<ChampionshipDetail>(`/championships/${campeonatoId}`)).data,
    retry: false,
  });
  const { data: equipo, isLoading, isError } = useQuery({
    queryKey: ["championship", campeonatoId, "equipo", equipoId],
    queryFn: async () => (await api.get<ChampionshipTeamDetail>(`/championships/${campeonatoId}/equipos/${equipoId}`)).data,
    retry: false,
  });

  const canManage = campeonato?.canManage ?? false;
  const crumbBase = [{ label: "Campeonatos", to: "/campeonatos" }, { label: campeonato?.nombre ?? "Campeonato", to: `/campeonatos/${campeonatoId}` }];

  if (isLoading) return <p className="text-sm text-ink-400">Cargando pareja…</p>;
  if (isError || !equipo || !campeonato) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs items={[...crumbBase, { label: "No disponible" }]} />
        <Card>
          <p className="text-sm text-ink-500">Esta pareja no existe o no tienes acceso a ella.</p>
        </Card>
      </div>
    );
  }

  return (
    <TeamFicha
      key={equipo.id}
      campeonato={campeonato}
      equipo={equipo}
      canManage={canManage}
      crumbBase={crumbBase}
      onSaved={() => {
        queryClient.invalidateQueries({ queryKey: ["championship", campeonatoId] });
      }}
      onDeleted={() => {
        queryClient.invalidateQueries({ queryKey: ["championship", campeonatoId] });
        queryClient.invalidateQueries({ queryKey: ["championships"] });
        navigate(`/campeonatos/${campeonatoId}`);
      }}
      navigateBack={() => navigate(`/campeonatos/${campeonatoId}`)}
    />
  );
}

function TeamFicha({
  campeonato,
  equipo,
  canManage,
  crumbBase,
  onSaved,
  onDeleted,
  navigateBack,
}: {
  campeonato: ChampionshipDetail;
  equipo: ChampionshipTeamDetail;
  canManage: boolean;
  crumbBase: { label: string; to: string }[];
  onSaved: () => void;
  onDeleted: () => void;
  navigateBack: () => void;
}) {
  const [nombre, setNombre] = useState(equipo.nombre ?? "");
  const [j1, setJ1] = useState<PlayerRef>({ id: equipo.jugador1.id });
  const [j2, setJ2] = useState<PlayerRef>({ id: equipo.jugador2.id });
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const save = useMutation({
    mutationFn: () => api.put(`/championships/${campeonato.id}/equipos/${equipo.id}`, { nombre: nombre.trim() || null, jugador1: j1, jugador2: j2 }),
    onSuccess: () => {
      toast.success("Pareja actualizada");
      onSaved();
    },
    onError: (error) => toast.error(apiError(error, "No se pudo actualizar la pareja")),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/championships/${campeonato.id}/equipos/${equipo.id}`),
    onSuccess: () => {
      toast.success("Pareja eliminada del campeonato");
      onDeleted();
    },
    onError: (error) => toast.error(apiError(error, "No se pudo eliminar la pareja")),
  });

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[...crumbBase, { label: teamLabel(equipo) }]} />

      <div className="flex min-w-0 items-center gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <Users2 className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold break-words text-ink-900">{teamLabel(equipo)}</h1>
          <p className="break-words text-ink-400">Pareja inscrita en {campeonato.nombre}</p>
        </div>
      </div>

      <Card>
        {canManage ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!j1 || !j2) {
                toast.error("Indica los dos jugadores de la pareja");
                return;
              }
              setConfirmSave(true);
            }}
            className="flex flex-col gap-4"
          >
            <p className="text-sm font-medium text-ink-600">Datos de la pareja</p>
            <Input
              id="equipo-nombre"
              label="Nombre de la pareja (opcional)"
              placeholder="Si lo dejas vacío se muestra «Jugador 1 / Jugador 2»"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full"
            />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <PlayerPicker label="Jugador 1" championshipId={campeonato.id} equipoId={equipo.id} initial={toPlayerOption(equipo.jugador1)} onChange={setJ1} />
              <PlayerPicker label="Jugador 2" championshipId={campeonato.id} equipoId={equipo.id} initial={toPlayerOption(equipo.jugador2)} onChange={setJ2} />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" onClick={navigateBack}>
                Volver al campeonato
              </Button>
              <Button type="button" variant="danger" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4" />
                Eliminar pareja
              </Button>
              <Button type="submit" disabled={save.isPending}>
                Guardar cambios
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-ink-400">Jugador 1</dt>
              <dd className="font-medium text-ink-800">{personName(equipo.jugador1)}</dd>
            </div>
            <div>
              <dt className="text-ink-400">Jugador 2</dt>
              <dd className="font-medium text-ink-800">{personName(equipo.jugador2)}</dd>
            </div>
          </dl>
        )}
      </Card>

      <Card className="flex flex-col gap-3">
        <p className="text-sm font-medium text-ink-600">Partidos de esta pareja ({equipo.partidos.length})</p>
        {!equipo.partidos.length ? (
          <p className="text-sm text-ink-400">Esta pareja todavía no tiene partidos asignados.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {equipo.partidos.map((m) => {
              const { a, b } = matchLabel(m);
              const isA = m.equipoAId === equipo.id;
              const rival = isA ? b : a;
              // El resultado se muestra desde el punto de vista de ESTA pareja (primero sus sets).
              const mine = isA ? m.setsA : m.setsB;
              const theirs = isA ? m.setsB : m.setsA;
              return (
                <li key={m.id} className="flex flex-col gap-2 rounded-xl border border-ink-100 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium break-words text-ink-800">
                      <span className="font-normal text-ink-300">vs</span> {rival}
                      {mine != null && theirs != null && (
                        <span className="ml-2 rounded-md bg-ink-50 px-1.5 py-0.5 text-xs font-bold whitespace-nowrap text-ink-700">
                          {mine} – {theirs}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-ink-400">
                      {m.fase ?? "Sin fase"} · {formatDateTime(m.startsAt)} · {m.court ? `${m.court.club.nombre} — ${m.court.name}` : "Pista por definir"}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <Badge tone={championshipTone(m.estado)}>{MATCH_STATUS_LABEL[m.estado]}</Badge>
                    {m.matchEnded && m.reservationId && (
                      <Link to={`/partidos/${m.reservationId}`} className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline">
                        Ver repetición
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <ConfirmDialog
        open={confirmSave}
        onOpenChange={setConfirmSave}
        title="Guardar cambios de la pareja"
        description={`¿Seguro que quieres guardar los cambios de "${teamLabel(equipo)}"? Sus partidos ya programados mantendrán la pareja con los nuevos jugadores.`}
        tone="primary"
        confirmLabel="Guardar"
        onConfirm={() => save.mutate()}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Eliminar pareja"
        description={`Se eliminará "${teamLabel(equipo)}" del campeonato y sus partidos se quedarán con la pareja por definir. Esta acción no se puede deshacer.`}
        tone="danger"
        confirmLabel="Eliminar"
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
